import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { ArrowRight } from 'lucide-react';
import { PhoneInput } from '../components/PhoneInput';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { useGTM } from '../hooks/useGTM';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CapturePage = () => {
  const { slug, token } = useParams();
  const [linkInfo, setLinkInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [returningMessage, setReturningMessage] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    whatsapp: '',
    data_nascimento: '',
  });
  // Meta Pixel — scoped to THIS tenant only (isolation guaranteed by URL).
  const { trackEvent } = useMetaPixel(linkInfo?.meta_pixel_id, linkInfo?.meta_pixel_test_event_code);
  // GTM — same tenant-isolation guarantee.
  useGTM(linkInfo?.gtm_container_id);

  useEffect(() => {
    fetchLinkInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  const fetchLinkInfo = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/public/info/${slug}/${token}`);
      setLinkInfo(data);
    } catch (err) {
      setError('Link não encontrado ou inativo');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Convert DD/MM/YYYY → ISO YYYY-MM-DD for backend
    let isoBirth = '';
    if (formData.data_nascimento) {
      const m = formData.data_nascimento.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!m) {
        setError('Data de nascimento inválida. Use o formato dia/mês/ano.');
        return;
      }
      const [, dd, mm, yyyy] = m;
      const day = parseInt(dd, 10);
      const month = parseInt(mm, 10);
      const year = parseInt(yyyy, 10);
      const dt = new Date(year, month - 1, day);
      if (
        dt.getFullYear() !== year ||
        dt.getMonth() !== month - 1 ||
        dt.getDate() !== day ||
        year < 1920 ||
        dt > new Date()
      ) {
        setError('Data de nascimento inválida.');
        return;
      }
      isoBirth = `${yyyy}-${mm}-${dd}`;
    }
    setSubmitting(true);
    setError('');
    try {
      // Try to obtain geolocation (non-blocking — user can deny and the
      // submission still proceeds without coordinates).
      const geo = await new Promise((resolve) => {
        if (!('geolocation' in navigator)) return resolve(null);
        let settled = false;
        const done = (val) => {
          if (settled) return;
          settled = true;
          resolve(val);
        };
        try {
          navigator.geolocation.getCurrentPosition(
            (pos) =>
              done({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                geo_accuracy: pos.coords.accuracy,
              }),
            () => done(null),
            { timeout: 6000, maximumAge: 60000, enableHighAccuracy: false }
          );
          // Hard timeout fallback in case the browser hangs
          setTimeout(() => done(null), 7000);
        } catch {
          done(null);
        }
      });
      const payload = { ...formData, data_nascimento: isoBirth || null, ...(geo || {}) };
      const { data } = await axios.post(`${BACKEND_URL}/api/public/submit/${slug}/${token}`, payload);
      // Fire Meta Pixel Lead event (tenant-scoped). Failures never block UX.
      trackEvent('Lead', {
        content_name: 'Supporter Signup',
        content_category: 'apoiador',
      });
      if (data.is_returning) {
        setReturningMessage('Olá novamente! Você já estava na nossa rede — redirecionando você para o WhatsApp.');
        setTimeout(() => {
          window.location.href = data.whatsapp_url;
        }, 1500);
      } else {
        window.location.href = data.whatsapp_url;
      }
    } catch (err) {
      setError('Erro ao enviar dados. Tente novamente.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error && !linkInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <p className="text-destructive font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 p-4">
      <Card className="w-full max-w-md overflow-hidden" data-testid="capture-page">
        {linkInfo?.capture_hero_image_url && (
          <div
            className="w-full aspect-[1200/628] bg-slate-100 overflow-hidden"
            data-testid="capture-hero"
          >
            <img
              src={linkInfo.capture_hero_image_url}
              alt="Cabeçalho da campanha"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        )}
        <CardHeader>
          <CardTitle className="text-3xl font-black tracking-tight">
            Você foi convidado(a)!
          </CardTitle>
          <CardDescription className="text-base pt-2">
            {linkInfo?.lideranca?.nome && (
              <>
                <span className="font-bold text-foreground">{linkInfo.lideranca.nome}</span> está
                te convidando para participar da rede.
              </>
            )}
          </CardDescription>
          <CardDescription
            className="text-sm pt-2 whitespace-pre-line"
            data-testid="capture-subtitle"
          >
            {linkInfo?.capture_subtitle ||
              'Preencha seus dados para continuar e entrar no grupo/canal do WhatsApp.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome Completo *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Seu nome completo"
                required
                data-testid="capture-nome-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="whatsapp">WhatsApp *</Label>
              <PhoneInput
                id="whatsapp"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                required
                data-testid="capture-whatsapp-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="data_nascimento">Data de nascimento</Label>
              <Input
                id="data_nascimento"
                type="text"
                value={formData.data_nascimento}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                  let masked = digits;
                  if (digits.length > 4) {
                    masked = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
                  } else if (digits.length > 2) {
                    masked = `${digits.slice(0, 2)}/${digits.slice(2)}`;
                  }
                  setFormData({ ...formData, data_nascimento: masked });
                }}
                placeholder="dd/mm/aaaa"
                inputMode="numeric"
                autoComplete="bday"
                maxLength={10}
                className="h-11 text-base"
                data-testid="capture-data-nascimento-input"
              />
            </div>
            {error && (
              <div className="text-sm text-destructive" data-testid="capture-error">
                {error}
              </div>
            )}
            {returningMessage && (
              <div className="text-sm text-emerald-700 bg-emerald-50 p-3 rounded border border-emerald-200" data-testid="capture-returning-msg">
                {returningMessage}
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="capture-submit-btn"
            >
              {submitting ? (
                <>Redirecionando...</>
              ) : (
                <>
                  Continuar para o WhatsApp
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center pt-2">
              Ao continuar, podemos solicitar sua localização aproximada para fins estatísticos.
              Seus dados serão compartilhados apenas com a coordenação da campanha. Participação voluntária.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
